import { useState, useEffect, useRef, ReactNode } from 'react'
import FilterPanelContext from './FilterPanelContext'
import { FilterSelect } from './FilterSelect'
import { FilterInput } from './FilterInput'
import AppButton from '../../atoms/AppButton/AppButton'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
    children: ReactNode
    onApply: (values: Record<string, unknown>) => void
    onReset: () => void
    onClose: () => void
    title?: string
    initialValues?: Record<string, unknown>
}

// Compound component interface — sin React.FC (punto 4)
interface FilterPanelComponent {
    (props: Props): ReactNode
    Select: typeof FilterSelect
    Input: typeof FilterInput
}

// ─── Main component ───────────────────────────────────────────────────────────

const FilterPanel: FilterPanelComponent = ({
    children,
    onApply,
    onReset,
    onClose,
    title = 'Filtros',
    initialValues = {},
}) => {
    const [values, setValues] = useState<Record<string, unknown>>(initialValues)

    // Sincroniza el estado interno si initialValues cambia desde el padre
    // (cubre el caso de uso donde el panel permanece montado).
    // Se usa una ref con el objeto previo para comparar por referencia estable,
    // evitando el problema de JSON.stringify con valores no serializables (Date, undefined, etc.)
    // y loops cuando el padre pasa un objeto literal nuevo en cada render.
    // La responsabilidad de memoizar initialValues recae en el padre (useMemo/useState).
    const prevInitialValuesRef = useRef(initialValues)
    useEffect(() => {
        if (prevInitialValuesRef.current !== initialValues) {
            prevInitialValuesRef.current = initialValues
            setValues(initialValues)
        }
    }, [initialValues])

    const setValue = (name: string, value: unknown) =>
        setValues((prev) => ({ ...prev, [name]: value }))

    const clearValue = (name: string) =>
        setValues((prev) => {
            const next = { ...prev }
            delete next[name]
            return next
        })

    const handleReset = () => {
        setValues({})
        onReset()
    }

    const handleApply = () => onApply(values)

    return (
        <FilterPanelContext.Provider value={{ values, setValue, clearValue }}>
            <div className="flex flex-col w-full bg-white rounded-2xl shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Cerrar"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex flex-col gap-5 px-5 py-5">
                    {children}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-200">
                    <AppButton
                        title="Resetear"
                        state="ghost_secondary"
                        onClick={handleReset}
                        className="flex-1"
                    />
                    <AppButton
                        title="Aplicar filtro"
                        state="primary"
                        onClick={handleApply}
                        className="flex-1"
                    />
                </div>
            </div>
        </FilterPanelContext.Provider>
    )
}

FilterPanel.Select = FilterSelect
FilterPanel.Input = FilterInput

export default FilterPanel
